import urls, { dashboardApiHost } from "@/constants/urls";
import { getFavoriteCampaign } from "@/helpers/storage.js";
import { STATUS_KEYS } from "@/views/my-contacts/constants";
import axios from "axios";

/**
 * Helper function for making API requests using Axios.
 * Constructs the full URL, adds authorization headers, and handles API errors.
 * @param {string} endpoint - The API endpoint (e.g., '/team_leader_contacts').
 * @param {Object} [options={}] - Axios request configuration options (method, params, data, headers, url).
 * @returns {Promise<Object>} The data from the API response.
 * @throws {Object} An error object if the API request fails.
 */
async function request(endpoint, options = {}) {
  const url = options?.url || `${dashboardApiHost}/orgarea/api/v1/donor_account/${endpoint}`;

  const config = {
    method: options.method || "get",
    params: options.params,
    data: options.data,
    headers: options.headers, // Allow custom headers (e.g., multipart/form-data)
  };

  try {
    const response = await axios(url, config);
    return response.data;
  } catch (error) {
    console.error("API request failed:", error);
    if (error.response) {
      const apiError = {
        message: error.response.data?.message || `HTTP error! Status: ${error.response.status}`,
        status: error.response.status,
        errors: error.response.data?.errors || [],
        originalError: error.response.data,
      };
      throw apiError;
    } else if (error.request) {
      console.error("API request error: No response received", error.request);
      throw { message: "Network Error: No response received from server." };
    } else {
      console.error("API request setup error:", error.message);
      throw { message: `Request setup error: ${error.message}` };
    }
  }
}

const state = {
  statusValue: null,
  selectedContacts: [],
  statisticsStatuses: {
    [STATUS_KEYS.NEW_LEADS]: 0,
    [STATUS_KEYS.NOT_ANSWERED]: 0,
    [STATUS_KEYS.IN_PROCESS]: 0,
    [STATUS_KEYS.NOT_RELEVANT]: 0,
    [STATUS_KEYS.DONATED]: 0,
  },
  expectedDonations: 0,
  searchFilters: {
    [STATUS_KEYS.NEW_LEADS]: false,
    [STATUS_KEYS.NOT_ANSWERED]: false,
    [STATUS_KEYS.IN_PROCESS]: false,
    [STATUS_KEYS.NOT_RELEVANT]: false,
    [STATUS_KEYS.DONATED]: false,
  },
  searchQuery: "",
  teamLeaderContacts: [],
  currentTeamLeaderContact: null,
  teamLeaderContactActions: [],

  teamLeaderContactsLoading: false,
  teamLeaderContactsError: null,
  teamLeaderContactsPagination: {
    page: 1,
    per_page: 10,
    total: 0,
  },
  teamLeaderContactsLastFetchParams: {},

  teamLeaderContactActionsLoading: false,
  teamLeaderContactActionsError: null,
  teamLeaderContactActionsPagination: {
    page: 1,
    per_page: 10,
    total: 0,
  },
  teamLeaderContactActionsLastFetchParams: {},

  campaignCurrency: null,
  campaignId: null,
  teamId: null,
  campaigns: [],
  teams: [],
};

const getters = {};

const mutations = {
  resetStorePagination(state) {
    state.teamLeaderContactsPagination = {
      page: 1,
      per_page: 10,
      total: 0,
    };
  },
  updateStatusValue(state, value) {
    state.statusValue = value;
  },
  setSelectedValue(state, value) {
    if (value) {
      state.selectedContacts.push(value);
    } else {
      state.selectedContacts = [];
    }
  },
  removeSelectedValue(state, value) {
    state.selectedContacts = state.selectedContacts.filter((id) => id !== value);
  },
  setFilters(state, value) {
    state.searchFilters = { ...state.searchFilters, ...value };
  },
  setSearchQuery(state, value) {
    state.searchQuery = value;
  },
  setStatisticsStatuses(state, statuses) {
    state.statisticsStatuses = statuses;
  },
  setExpectedDonations(state, amount) {
    state.expectedDonations = amount;
  },
  setTeamLeaderContacts(state, contacts) {
    state.teamLeaderContacts = contacts;
  },
  setTeamLeaderContact(state, contact) {
    state.currentTeamLeaderContact = contact;
  },
  setTeamLeaderContactActions(state, actions) {
    state.teamLeaderContactActions = actions;
  },
  setTeamLeaderContactsLoading(state, loading) {
    state.teamLeaderContactsLoading = loading;
  },
  setTeamLeaderContactsError(state, error) {
    state.teamLeaderContactsError = error;
  },
  setTeamLeaderContactsPagination(state, pagination) {
    state.teamLeaderContactsPagination = pagination;
  },
  setTeamLeaderContactsLastFetchParams(state, params) {
    state.teamLeaderContactsLastFetchParams = params;
  },
  setTeamLeaderContactActionsLoading(state, loading) {
    state.teamLeaderContactActionsLoading = loading;
  },
  setTeamLeaderContactActionsError(state, error) {
    state.teamLeaderContactActionsError = error;
  },
  setTeamLeaderContactActionsPagination(state, pagination) {
    state.teamLeaderContactActionsPagination = pagination;
  },
  setTeamLeaderContactActionsLastFetchParams(state, params) {
    state.teamLeaderContactActionsLastFetchParams = params;
  },
  setCampaigns(state, campaigns) {
    state.campaigns = campaigns;
  },
  setTeams(state, teams) {
    state.teams = teams;
  },
  setCampaignId(state, campaignId) {
    state.campaignId = campaignId;
  },
  setTeamId(state, teamId) {
    state.teamId = teamId;
  },
  setCampaignCurrency(state, campaignCurrency) {
    state.campaignCurrency = campaignCurrency;
  },
};

const actions = {
  setStatusValue({ commit }, value) {
    commit("updateStatusValue", value);
  },
  selectedMyContactsHandler({ commit }, value) {
    commit("setSelectedValue", value);
  },
  unSelectMyContactsHandler({ commit }, value) {
    commit("removeSelectedValue", value);
  },
  setFilterValues({ commit }, value) {
    commit("setFilters", value);
  },
  setSearchQuery({ commit }, value) {
    commit("setSearchQuery", value);
  },

  /**
   * Fetches team leader contacts from the API.
   * @param {Object} context - Vuex action context.
   * @param {Object} params - API request parameters (e.g., for pagination, filtering).
   * @returns {Promise<Object>} The API response, including contact data and pagination meta.
   * @throws {Error} If the API request fails.
   */
  async fetchTeamLeaderContacts({ commit, state }, params) {
    const orgId = localStorage.getItem("orgId");
    const url = urls.orgTeamLeaderContacts(orgId, state.campaignId);

    const response = await request("team_leader_contacts", { method: "GET", params, url });

    const statusCountList = response.meta.status_count_list;
    const statisticsStatuses = {
      [STATUS_KEYS.NEW_LEADS]: 0,
      [STATUS_KEYS.NOT_ANSWERED]: 0,
      [STATUS_KEYS.IN_PROCESS]: 0,
      [STATUS_KEYS.NOT_RELEVANT]: 0,
      [STATUS_KEYS.DONATED]: 0,
    };
    for (const statusCount of statusCountList) {
      statisticsStatuses[statusCount.status] = statusCount.count;
    }
    const expectedDonations = response.meta.expected_donation_amount_total;

    commit("setExpectedDonations", expectedDonations);
    commit("setStatisticsStatuses", statisticsStatuses);
    commit("setTeamLeaderContacts", response.data);
    return response;
  },

  /**
   * Creates a new team leader contact.
   * @param {Object} context - Vuex action context.
   * @param {Object} contactData - Data for the new contact.
   * @returns {Promise<Object>} The created contact data.
   * @throws {Error} If the API request fails.
   */
  async createTeamLeaderContact(_, contactData) {
    const response = await request("team_leader_contacts", {
      method: "POST",
      data: contactData,
    });
    return response.data;
  },

  /**
   * Fetches a single team leader contact by ID.
   * @param {Object} context - Vuex action context.
   * @param {String|Number} contactId - The ID of the contact to fetch.
   * @returns {Promise<Object>} The fetched contact data.
   * @throws {Error} If the API request fails.
   */
  async fetchTeamLeaderContactById({ commit, state }, contactId) {
    const orgId = localStorage.getItem("orgId");
    const url =
      urls.orgTeamLeaderContacts(orgId, state.campaignId || getFavoriteCampaign()) +
      `/${contactId}`;
    const response = await request("team_leader_contacts", { method: "GET", url });

    commit("setTeamLeaderContact", response.data);
    return response.data;
  },

  /**
   * Updates an existing team leader contact.
   * @param {Object} context - Vuex action context.
   * @param {Object} payload - Contains contactId and contactData.
   * @param {String|Number} payload.contactId - The ID of the contact to update.
   * @param {Object} payload.contactData - The new data for the contact.
   * @returns {Promise<Object>} The updated contact data.
   * @throws {Error} If the API request fails.
   */
  async updateTeamLeaderContact({ commit, state }, { contactId, contactData }) {
    const orgId = localStorage.getItem("orgId");
    const url =
      urls.orgTeamLeaderContacts(orgId, state.campaignId || getFavoriteCampaign()) +
      `/${contactId}`;

    const response = await request("team_leader_contacts", {
      method: "PUT",
      data: contactData,
      url,
    });
    commit("setTeamLeaderContact", response.data);
    return response.data;
  },

  /**
   * Updates the status of a team leader contact.
   * @param {Object} context - Vuex action context.
   * @param {Object} payload - Contains contactId and statusData.
   * @param {String|Number} payload.contactId - The ID of the contact.
   * @param {Object} payload.statusData - Data for the status update (e.g., { status_key: 'new_status' }).
   * @returns {Promise<Object>} The updated contact data.
   * @throws {Error} If the API request fails.
   */
  async updateTeamLeaderContactStatus({ commit, state }, { contactId, statusData }) {
    const orgId = localStorage.getItem("orgId");
    const url =
      urls.orgTeamLeaderContacts(orgId, state.campaignId || getFavoriteCampaign()) +
      `/${contactId}/status`;

    const response = await request("team_leader_contacts", {
      method: "PATCH",
      data: statusData,
      url,
    });
    commit("setTeamLeaderContact", response.data);
    return response.data;
  },

  /**
   * Fetches actions associated with a team leader contact.
   * @param {Object} context - Vuex action context.
   * @param {Object} payload - Contains contactId and params.
   * @param {String|Number} payload.contactId - The ID of the contact.
   * @param {Object} payload.params - API request parameters (e.g., for pagination).
   * @returns {Promise<Object>} The API response, including actions data and pagination meta.
   * @throws {Error} If the API request fails.
   */
  async fetchTeamLeaderContactActions({ commit }, { contactId, params }) {
    const response = await request(`team_leader_contacts/${contactId}/actions`, {
      method: "GET",
      params,
    });
    commit("setTeamLeaderContactActions", response.data);
    if (response.meta) {
      commit("setTeamLeaderContactActionsPagination", {
        page: response.meta.page,
        per_page: response.meta.per_page,
        total: response.meta.total,
      });
    }
    return response;
  },

  /**
   * Creates a new action for a team leader contact.
   * @param {Object} context - Vuex action context.
   * @param {Object} payload - Contains contactId and actionData.
   * @param {String|Number} payload.contactId - The ID of the contact.
   * @param {Object} payload.actionData - Data for the new action.
   * @returns {Promise<Object>} The created action data.
   * @throws {Error} If the API request fails.
   */
  async createTeamLeaderContactAction(_, { contactId, actionData }) {
    const response = await request(`team_leader_contacts/${contactId}/actions`, {
      method: "POST",
      data: actionData,
    });
    return response.data;
  },

  /**
   * Import team leader contacts from an XLSX file.
   * @param {Object} _ - Vuex action context (unused).
   * @param {Object} payload - Contains campaignId, teamId, and file to import.
   * @param {Number|String} payload.campaignId - Campaign ID.
   * @param {Number|String} payload.teamId - Team ID.
   * @param {File} payload.file - XLSX file selected by the user.
   * @returns {Promise<Object>} The API response.
   */
  async importTeamLeaderContactsXlsx(_, { campaignId, teamId, file }) {
    const url = urls.orgTeamLeaderContactsImport(
      localStorage.getItem("orgId"),
      campaignId || getFavoriteCampaign()
    );

    const formData = new FormData();
    formData.append("campaign_id", campaignId);
    formData.append("team_id", teamId);
    formData.append("file", file);

    const response = await request("team_leader_contacts/import/xlsx", {
      method: "POST",
      data: formData,
      headers: { "Content-Type": "multipart/form-data" },
      url,
    });

    return response.data;
  },

  /**
   * Deletes a team leader contact by ID.
   * @param {Object} context - Vuex action context.
   * @param {String|Number} contactId - The ID of the contact to delete.
   * @returns {Promise<void>} Resolves when deletion is successful.
   * @throws {Error} If the API request fails.
   */
  async deleteTeamLeaderContact({ commit, state }, contactId) {
    const orgId = localStorage.getItem("orgId");
    const url =
      urls.orgTeamLeaderContacts(orgId, state.campaignId || getFavoriteCampaign()) +
      `/${contactId}`;

    await request("team_leader_contacts", { method: "DELETE", url });
    commit("setTeamLeaderContact", null);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
